import { useEffect, useState } from 'react';
import { FileText, Link2 } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { Attachment } from '../types';

// Human-readable file size: "12 KB", "3.4 MB".
function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Side-panel section listing every file attached to the project — on tasks or on
// the timeline — since all attachments carry a project_id. Matches the
// ProjectTasksPanel section/header markup and the muted, thin-rule side-panel
// language (.pdp-docs*). No pills, no boxes.
export function ProjectDocuments({ projectId }: { projectId: number }) {
  const { t } = useT();
  const [docs, setDocs] = useState<Attachment[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    api.getProjectAttachments(projectId)
      .then((all) => { if (active) setDocs((all as Attachment[]) ?? []); })
      .catch(() => {})
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [projectId]);

  return (
    <div className="detail-section" style={{ marginBottom: 'var(--s16)' }}>
      <div className="detail-label" style={{ marginBottom: 'var(--s8)' }}>
        {t('documents.title')}{loaded ? ` (${docs.length})` : ''}
      </div>
      {!loaded ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('common.loading')}</div>
      ) : docs.length === 0 ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('documents.empty')}</div>
      ) : (
        <div className="pdp-docs">
          {docs.map((doc) => (
            <a
              key={doc.id}
              href={doc.kind === 'link' && doc.url ? doc.url : `/api/attachments/${doc.id}/download`}
              className="pdp-doc"
              title={doc.original_filename}
              {...(doc.kind === 'link' ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true })}
            >
              <span className="pdp-doc-icon">{doc.kind === 'link' ? <Link2 size={14} /> : <FileText size={14} />}</span>
              <span className="pdp-doc-main">
                <span className="pdp-doc-name">{doc.original_filename}</span>
                {doc.linked_task_title && (
                  <span className="pdp-doc-by">{t('docs.linkedTask', { title: doc.linked_task_title })}</span>
                )}
                {doc.uploaded_by_user_name && (
                  <span className="pdp-doc-by">{doc.uploaded_by_user_name}</span>
                )}
              </span>
              {doc.kind !== 'link' && <span className="pdp-doc-size">{formatBytes(doc.file_size)}</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
